import { useEffect, useRef } from 'react'
import { useStoreActions, useStoreState } from 'store/hooks'
import { getGraphQLServerURI } from 'utils/getEnvConfig'

// Mutation events that should trigger a store refresh
const MUTATION_EVENTS = [
    'pageCreated',
    'pageContentUpdated',
    'pageSettingsUpdated',
    'pageActivated',
    'pageDeactivated',
    'pageDeleted',
    'pagesPublished',
    'pagesReordered',
    'blockCreated',
    'blockUpdated',
    'blockDeleted',
    'blocksPublished',
    'elementDefinitionsUpdated',
]

/**
 * App-level WebSocket hook that listens to MCP events and refreshes the store.
 * This runs regardless of which page the user is on.
 */
export function useMcpWebSocket() {
    const selectedWebsite = useStoreState((state) => state.model.selectedWebsite)
    const setSelectedWebsite = useStoreActions((actions) => actions.model.setSelectedWebsite)
    const wsRef = useRef<WebSocket | null>(null)
    const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (!selectedWebsite?.id) return

        const serverURI = getGraphQLServerURI()
        const wsURL =
            serverURI.replace(/^http/, 'ws').replace('/graphql', '') + `/ws/mcp-events?websiteID=${selectedWebsite.id}`

        const ws = new WebSocket(wsURL)
        wsRef.current = ws

        ws.onmessage = (msg) => {
            try {
                const event = JSON.parse(msg.data)

                // Persist last page event so AI Live can pick it up when opened
                if (event.pageID) {
                    sessionStorage.setItem('mcpLastPageID', event.pageID)
                    sessionStorage.setItem('mcpLastEvent', JSON.stringify(event))
                }

                if (MUTATION_EVENTS.includes(event.type)) {
                    // Debounce refresh — batch rapid changes
                    if (refreshTimer.current) clearTimeout(refreshTimer.current)
                    refreshTimer.current = setTimeout(() => {
                        if (selectedWebsite?.id) {
                            setSelectedWebsite(selectedWebsite.id)
                        }
                    }, 1000)
                }
            } catch {
                // ignore
            }
        }

        return () => {
            ws.close()
            wsRef.current = null
            if (refreshTimer.current) clearTimeout(refreshTimer.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWebsite?.id])
}
